import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    endOfMonth,
    endOfQuarter,
    endOfYear,
    format,
    formatISO,
    getQuarter,
    getYear,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subMonths,
    subQuarters,
    subYears,
} from "date-fns";
import classNames from "classnames";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { Picker } from "@design-system/Picker/Picker.lib";
import styles from "./ApplyCloseTemplateDrawer.module.scss";
import {
    getDefaultPeriodValue,
    type PeriodType,
} from "./closePeriodSelector.utils";

interface PeriodOption {
    value: string;
    label: string;
    startDate: string;
    endDate: string;
}

const PERIOD_TYPE_OPTIONS: Array<{ value: PeriodType; label: string }> = [
    { value: "month", label: "Month" },
    { value: "quarter", label: "Quarter" },
    { value: "year", label: "Year" },
    { value: "custom", label: "Custom" },
];

function buildMonthOptions(): PeriodOption[] {
    const today = new Date();
    return Array.from({ length: 24 }, (_, i) => {
        const date = subMonths(today, i);
        return {
            value: format(date, "yyyy-MM"),
            label: format(date, "MMMM yyyy"),
            startDate: formatISO(startOfMonth(date), {
                representation: "date",
            }),
            endDate: formatISO(endOfMonth(date), { representation: "date" }),
        };
    });
}

function buildQuarterOptions(): PeriodOption[] {
    const today = new Date();
    return Array.from({ length: 20 }, (_, i) => {
        const date = subQuarters(today, i);
        const q = getQuarter(date);
        const year = getYear(date);
        return {
            value: `${year}-Q${q}`,
            label: `Q${q} ${year}`,
            startDate: formatISO(startOfQuarter(date), {
                representation: "date",
            }),
            endDate: formatISO(endOfQuarter(date), { representation: "date" }),
        };
    });
}

function buildYearOptions(): PeriodOption[] {
    const today = new Date();
    return Array.from({ length: 10 }, (_, i) => {
        const date = subYears(today, i);
        const year = getYear(date);
        return {
            value: String(year),
            label: String(year),
            startDate: formatISO(startOfYear(date), { representation: "date" }),
            endDate: formatISO(endOfYear(date), { representation: "date" }),
        };
    });
}

interface ClosePeriodSelectorProps {
    startDate: string | null | undefined;
    endDate: string | null | undefined;
    onStartDateChange: (value: string | null) => void;
    onEndDateChange: (value: string | null) => void;
}

function detectPeriodFromDates(
    startDate: string | null | undefined,
    endDate: string | null | undefined,
    allOptions: Record<Exclude<PeriodType, "custom">, PeriodOption[]>,
): { type: PeriodType; value: string } | null {
    if (!startDate || !endDate) {
        return null;
    }
    for (const periodType of ["month", "quarter", "year"] as const) {
        const match = allOptions[periodType].find(
            (o) => o.startDate === startDate && o.endDate === endDate,
        );
        if (match) {
            return { type: periodType, value: match.value };
        }
    }
    return { type: "custom", value: "" };
}

export const ClosePeriodSelector = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
}: ClosePeriodSelectorProps) => {
    const monthOptions = useMemo(() => buildMonthOptions(), []);
    const quarterOptions = useMemo(() => buildQuarterOptions(), []);
    const yearOptions = useMemo(() => buildYearOptions(), []);

    const optionsByType = useMemo(
        () => ({
            month: monthOptions,
            quarter: quarterOptions,
            year: yearOptions,
        }),
        [monthOptions, quarterOptions, yearOptions],
    );

    const [periodType, setPeriodType] = useState<PeriodType>(() => {
        const detected = detectPeriodFromDates(
            startDate,
            endDate,
            optionsByType,
        );
        return detected?.type ?? "month";
    });
    const [periodValue, setPeriodValue] = useState<string>(() => {
        const detected = detectPeriodFromDates(
            startDate,
            endDate,
            optionsByType,
        );
        if (detected && detected.type !== "custom") {
            return detected.value;
        }
        return getDefaultPeriodValue("month");
    });

    // Track the last dates we applied internally, to detect external changes.
    const lastAppliedDatesRef = useRef({ startDate, endDate });

    useEffect(() => {
        const last = lastAppliedDatesRef.current;
        if (startDate === last.startDate && endDate === last.endDate) {
            return;
        }
        lastAppliedDatesRef.current = { startDate, endDate };
        const detected = detectPeriodFromDates(
            startDate,
            endDate,
            optionsByType,
        );
        if (!detected) {
            return;
        }
        setPeriodType(detected.type);
        if (detected.type !== "custom") {
            setPeriodValue(detected.value);
        }
    }, [startDate, endDate, optionsByType]);

    const applyPeriodOption = useCallback(
        (options: PeriodOption[], value: string) => {
            const option = options.find((o) => o.value === value);
            if (option) {
                lastAppliedDatesRef.current = {
                    startDate: option.startDate,
                    endDate: option.endDate,
                };
                onStartDateChange(option.startDate);
                onEndDateChange(option.endDate);
            }
        },
        [onStartDateChange, onEndDateChange],
    );

    const handlePeriodTypeChange = useCallback(
        (type: PeriodType | null) => {
            if (type === null) {
                return;
            }
            setPeriodType(type);
            if (type === "custom") {
                return;
            }
            const defaultValue = getDefaultPeriodValue(type);
            setPeriodValue(defaultValue);
            applyPeriodOption(optionsByType[type], defaultValue);
        },
        [applyPeriodOption, optionsByType],
    );

    const handlePeriodValueChange = useCallback(
        (value: string | null) => {
            if (value === null || periodType === "custom") {
                return;
            }
            setPeriodValue(value);
            applyPeriodOption(optionsByType[periodType], value);
        },
        [periodType, applyPeriodOption, optionsByType],
    );

    const currentOptions =
        periodType !== "custom" ? optionsByType[periodType] : [];

    return (
        <div className={styles.periodSelectorRow}>
            <Field
                className={styles.periodSelectItem}
                label="Period"
                control="button"
            >
                <Picker.Select
                    items={PERIOD_TYPE_OPTIONS}
                    value={periodType}
                    onValueChange={handlePeriodTypeChange}
                    trigger={
                        <Picker.Button size="xl" variant="primary" block />
                    }
                />
            </Field>

            {periodType === "custom" ? (
                <>
                    <Field
                        className={styles.periodSelectItem}
                        label="Start date"
                        control="button"
                    >
                        <Field.Control
                            render={(controlProps) => (
                                <DatePicker
                                    mode="day"
                                    variant="panel-input"
                                    clearable={false}
                                    className={
                                        styles.applyCloseTemplateDatePickerInput
                                    }
                                    value={startDate ?? undefined}
                                    onValueChange={(value) =>
                                        onStartDateChange(value ?? null)
                                    }
                                    placeholder="Select start date..."
                                    buttonProps={{
                                        variant: "primary",
                                        size: "xl",
                                        block: true,
                                    }}
                                    fieldControlProps={pickFieldControlAriaProps(
                                        controlProps,
                                    )}
                                />
                            )}
                        />
                    </Field>
                    <Field
                        className={styles.periodSelectItem}
                        label="End date"
                        control="button"
                    >
                        <Field.Control
                            render={(controlProps) => (
                                <DatePicker
                                    mode="day"
                                    variant="panel-input"
                                    clearable={false}
                                    className={
                                        styles.applyCloseTemplateDatePickerInput
                                    }
                                    value={endDate ?? undefined}
                                    onValueChange={(value) =>
                                        onEndDateChange(value ?? null)
                                    }
                                    placeholder="Select end date..."
                                    buttonProps={{
                                        variant: "primary",
                                        size: "xl",
                                        block: true,
                                    }}
                                    fieldControlProps={pickFieldControlAriaProps(
                                        controlProps,
                                    )}
                                />
                            )}
                        />
                    </Field>
                </>
            ) : (
                <div
                    className={classNames(
                        styles.periodSelectItem,
                        styles.periodRangeItem,
                    )}
                >
                    <Picker.Select
                        items={currentOptions}
                        value={periodValue}
                        onValueChange={handlePeriodValueChange}
                        trigger={
                            <Picker.Button
                                size="xl"
                                variant="primary"
                                block
                                aria-label="Period range"
                            />
                        }
                    />
                </div>
            )}
        </div>
    );
};
